import { ChangeDetectionStrategy, Component, ChangeDetectorRef, AfterViewInit, OnInit, viewChild, Signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateDirective, TranslatePipe } from '@ngx-translate/core';
import { RouterBaseService, UtilityService } from '@tdwb/core';

import { SelectableInputPanelComponent } from '../../../components/selectable-input-panel/selectable-input-panel.component';
import { FormRowComponent } from '../../../components/form/row/form-row.component';
import { NotificationComponent } from '../../../components/notification/notification.component';
import { FormComponent } from '../../../components/form/form.component';
import { RadioGroupComponent } from '../../../components/radio/radio-group.component';
import { InputStatusIndicatorDirective } from '../../../components/form/input-status/input-status-indicator.directive';
import { CheckboxComponent } from '../../../components/checkbox/checkbox.component';
import { InputGroupFieldComponent } from '../../../components/form/input-group/input-group-field.component';
import { ModalCloseReason } from '../../../components/modal/modal.domain';
import { ModalCloseDirective } from '../../../components/modal/components/modal-close/modal-close-button.directive';
import { AddressUsageCd } from '../../../domain/user-profile.domain';
import { ButtonComponent } from '../../../components/button/button.component';
import { InputDirective } from '../../../components/input/input.directive';
import { DividerComponent } from '../../../components/panel/divider.component';
import { RadioComponent } from '../../../components/radio/radio.component';
import { InputGroupComponent } from '../../../components/form/input-group/input-group.component';
import { AddressComponent } from '../../../features/address/address.component';
import { PageActionPanelComponent } from '../../../features/page/page-action-panel.component';
import { InputGroupLabelComponent } from '../../../components/form/input-group/input-group-label.component';
import { InputErrorsComponent } from '../../../components/form/input-errors/input-errors.component';

import { W8BenFormTooltipComponent } from '../w8ben-form-tooltip/w8ben-form-tooltip.component';

import { W8BenFormService } from '../w8ben-form.service';
import { W8BenContactAddress, W8BenModalName, W8BenProfile, W8BenTaggingFlowType } from '../w8ben-form.domain';
import { W8BenFormAnalyticsService } from '../w8ben-form-analytics.service';
import { W8benFormAnalytics } from '../w8ben-form.analytics';

export type W8BenUpdateStep = 'permanent_residence' | 'contact_us' | 'update';

@Component({
  selector: 'td-wb-w8ben-update',
  templateUrl: 'w8ben-form-update.component.html',
  styleUrls: ['./w8ben-form-update.component.scss'],
  imports: [
    FormComponent,
    RadioGroupComponent,
    FormsModule,
    TranslatePipe,
    TranslateDirective,
    InputDirective,
    InputErrorsComponent,
    InputStatusIndicatorDirective,
    InputGroupLabelComponent,
    InputGroupFieldComponent,
    SelectableInputPanelComponent,
    RadioComponent,
    NotificationComponent,
    FormRowComponent,
    InputGroupComponent,
    W8BenFormTooltipComponent,
    AddressComponent,
    CheckboxComponent,
    DividerComponent,
    PageActionPanelComponent,
    ButtonComponent,
    ModalCloseDirective
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})

export class W8BenFormUpdateComponent implements OnInit, AfterViewInit {
  protected isPermanentResidence: boolean | null = null;
  protected sin: string | null = null;
  protected tin: string | null = null;
  protected currentStep: W8BenUpdateStep = 'permanent_residence';
  protected showMailingAddress: boolean = false;
  protected profile: W8BenProfile;
  protected modalCloseReason: ModalCloseReason = ModalCloseReason.ESC;
  protected readonly allowableTinCharactersRegex: RegExp = W8BenFormService.TIN_REGEX;

  readonly homeAddress: Signal<AddressComponent> = viewChild<AddressComponent>('homeAddress');
  readonly mailingAddress: Signal<AddressComponent> = viewChild<AddressComponent>('mailingAddress');
  readonly addressForm: Signal<FormComponent> = viewChild<FormComponent>('addressForm');

  get isPermanentResidenceStep(): boolean {
    return this.currentStep === 'permanent_residence';
  }

  get isUpdateProfileStep(): boolean {
    return this.currentStep === 'update';
  }

  constructor(protected changeDetectorRef: ChangeDetectorRef,
              protected w8BenFormService: W8BenFormService,
              private routerService: RouterBaseService,
              private w8benFormAnalyticsService: W8BenFormAnalyticsService) {
    // Deep copy here is because data is two way binding in address component
    this.profile = UtilityService.deepClone(this.w8BenFormService.profile);
    this.isPermanentResidence = this.routerService.getParamsValue('isPermanentResidence') as boolean;

    if (this.isPermanentResidence) {
      this.setStep('update')
    }
  }

  ngOnInit(): void {
    this.sin = this.w8BenFormService.getIdentification();
    this.tin = this.w8BenFormService.getIdentification('SSN');
    this.showMailingAddress = this.w8BenFormService.haveMailingAddress;

    if (!this.isAddressUpdateAllowed()) {
      this.setStep('contact_us', 'f');
    }

    if (this.isPermanentResidence === null && this.isAddressUpdateAllowed()) {
      this.setAnalytics('n/a');
    }
  }

  ngAfterViewInit(): void {
    const updatedAddress: Array<W8BenContactAddress> = this.w8BenFormService.updates.streetAddresses ?? [];
    if (updatedAddress?.length > 0) {
      for (const aStreetAddress of updatedAddress) {
        if (aStreetAddress.usageCd === AddressUsageCd.PRIMARY_RESIDENCE) {
          this.homeAddress()?.setSelectedAddress(aStreetAddress.address);
        } else if (aStreetAddress.usageCd === AddressUsageCd.CUSTOMER_MAILING) {
          this.mailingAddress()?.setSelectedAddress(aStreetAddress.address);
        }
      }
    }
  }

  protected getModalId(): string {
    return this.w8benFormAnalyticsService.getModalId();
  }

  protected onBackClick(): void {
    this.showMailingAddress = false;
    this.w8BenFormService.haveMailingAddress = false;
    if (this.isPermanentResidenceStep || !this.isAddressUpdateAllowed()) {
      this.routerService.go('modal.w8ben.preview', { retainData: true });
    } else {
      this.tin = null;
      this.w8BenFormService.setIdentification('', 'SSN');
      this.setStep('permanent_residence');
    }
  }

  protected reviewChanges(): void {
    if (this.addressForm().valid) {
      this.onContinueClick();
    }
  }

  protected onContinueClick(): void {
    if (this.isPermanentResidenceStep) {
      if (this.isPermanentResidence) {
        this.setStep('update');
      } else {
        this.setStep('contact_us', 'e');
      }
    } else {
      this.w8BenFormService.haveMailingAddress = this.showMailingAddress;
      this.w8BenFormService.updates.streetAddresses = [...this.getUpdatedAddress()];
      this.w8BenFormService.setIdentification(this.tin ?? '', 'SSN');
      this.routerService.go('modal.w8ben.tax_residency_benefits', { hasUpdates: true });
    }
  }

  protected getUpdatedAddress(): Array<W8BenContactAddress> {
    const address: Array<W8BenContactAddress> = [];
    address.push({
      usageCd: AddressUsageCd.PRIMARY_RESIDENCE,
      address: { ...this.homeAddress()?.getSelectedAddress(), addressLine: undefined }
    });
    if (this.showMailingAddress) {
      address.push({
        usageCd: AddressUsageCd.CUSTOMER_MAILING,
        address: { ...this.mailingAddress()?.getSelectedAddress(), addressLine: undefined }
      });
    }
    return address;
  }

  protected onClickTinInput(): void {
    this.w8benFormAnalyticsService.emitLinkClickEvent('w8ben:tin:update', false);
  }

  private isAddressUpdateAllowed(): boolean {
    return this.w8BenFormService.getTaxResidency() === '' ||
           this.w8BenFormService.isTaxResidencyCanada();
  }

  private setStep(step: W8BenUpdateStep, flow: W8BenTaggingFlowType = 'n/a'): void {
    this.currentStep = step;
    this.setAnalytics(flow);
  }

  private setAnalytics(flow: W8BenTaggingFlowType): void {
    switch (this.currentStep) {
      case 'permanent_residence':
        this.w8benFormAnalyticsService.setTaggingData(W8BenModalName.UPDATE_PR, flow);
        this.w8benFormAnalyticsService.emitPageChange(W8benFormAnalytics.getModalEvent(W8BenModalName.UPDATE_PR));
        break;
      case 'update':
        this.w8benFormAnalyticsService.setTaggingData(W8BenModalName.DETAILS, flow);
        this.w8benFormAnalyticsService.emitPageChange(W8benFormAnalytics.getModalEvent(W8BenModalName.DETAILS));
        break;
      case 'contact_us':
        this.w8benFormAnalyticsService.setTaggingData(W8BenModalName.CONTACT_US, flow);
        this.w8benFormAnalyticsService.emitPageChange(W8benFormAnalytics.getModalEvent(W8BenModalName.CONTACT_US, flow, 'complete'));
        break;
    }
  }
}
